import React, { useRef, useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Lock, Unlock, Trash2, Download, Edit, Eye, EyeOff, Tag, Gift, ArrowRight, Copy, BookOpen } from 'lucide-react';
import { ICON_MAP } from './DeckIconPicker';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    rectSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import DragHandle from './DragHandle';
import AccessControl from '../AccessControl/AccessControl';
import './DeckGrid.css';

export const DECK_COLORS: Record<string, string> = {
    'blue-default': '#185FA5',
    'blue-graia':   '#003BD1',
    'blue-deep':    '#042563',
    'blue-night':   '#1E3A5F',
    'blue-royal':   '#1E40AF',
    'blue-vivid':   '#1D4ED8',
    'blue-medium':  '#2563EB',
    'blue-cobalt':  '#2E4DB0',
    'blue-light':   '#3B82F6',
    'blue-sky':     '#60A5FA',
    'navy':         '#075985',
    'teal-night':   '#164E63',
    'teal':         '#0F766E',
    'cyan-dark':    '#0891B2',
    'cyan':         '#0EA5E9',
    // Greens
    'green-deep':    '#14532D',
    'green-forest':  '#166534',
    'green-emerald': '#065F46',
    'green-pine':    '#134E4A',
    'green-medium':  '#15803D',
    'green-sage':    '#4A7C59',
    'green-olive':   '#3F6212',
    'green-bright':  '#16A34A',
    // Gold / Yellow (baixa saturação)
    'gold-deep':     '#78350F',
    'gold-warm':     '#92400E',
    'gold-medium':   '#B45309',
    'amber':         '#D97706',
    'gold-light':    '#CA8A04',
    // Bege / Neutros quentes
    'beige-dark':    '#57534E',
    'beige':         '#78716C',
    'warm-brown':    '#6B5B45',
    // Roxo sutil
    'purple-deep':   '#4C1D95',
    'purple-soft':   '#5B21B6',
};

export const DECK_COLOR_OPTIONS = [
    // Azuis
    { id: 'blue-default', hex: '#185FA5', label: 'Azul Padrão' },
    { id: 'blue-graia',   hex: '#003BD1', label: 'Azul GRAIA' },
    { id: 'blue-deep',    hex: '#042563', label: 'Azul Profundo' },
    { id: 'blue-night',   hex: '#1E3A5F', label: 'Azul Noite' },
    { id: 'blue-royal',   hex: '#1E40AF', label: 'Azul Royal' },
    { id: 'blue-vivid',   hex: '#1D4ED8', label: 'Azul Vivo' },
    { id: 'blue-medium',  hex: '#2563EB', label: 'Azul Médio' },
    { id: 'blue-cobalt',  hex: '#2E4DB0', label: 'Azul Cobalto' },
    { id: 'blue-light',   hex: '#3B82F6', label: 'Azul Claro' },
    { id: 'blue-sky',     hex: '#60A5FA', label: 'Azul Céu' },
    { id: 'navy',         hex: '#075985', label: 'Azul Marinho' },
    { id: 'teal-night',   hex: '#164E63', label: 'Teal Noite' },
    { id: 'teal',         hex: '#0F766E', label: 'Teal' },
    { id: 'cyan-dark',    hex: '#0891B2', label: 'Ciano Escuro' },
    { id: 'cyan',         hex: '#0EA5E9', label: 'Ciano' },
    // Verdes
    { id: 'green-deep',    hex: '#14532D', label: 'Verde Profundo' },
    { id: 'green-forest',  hex: '#166534', label: 'Verde Floresta' },
    { id: 'green-emerald', hex: '#065F46', label: 'Esmeralda' },
    { id: 'green-pine',    hex: '#134E4A', label: 'Verde Pinho' },
    { id: 'green-medium',  hex: '#15803D', label: 'Verde Médio' },
    { id: 'green-sage',    hex: '#4A7C59', label: 'Verde Sálvia' },
    { id: 'green-olive',   hex: '#3F6212', label: 'Verde Oliva' },
    { id: 'green-bright',  hex: '#16A34A', label: 'Verde Vivo' },
    // Dourados / Âmbar
    { id: 'gold-deep',     hex: '#78350F', label: 'Marrom Dourado' },
    { id: 'gold-warm',     hex: '#92400E', label: 'Cobre' },
    { id: 'gold-medium',   hex: '#B45309', label: 'Ouro Médio' },
    { id: 'amber',         hex: '#D97706', label: 'Âmbar' },
    { id: 'gold-light',    hex: '#CA8A04', label: 'Ouro Claro' },
    // Bege / Neutros
    { id: 'beige-dark',    hex: '#57534E', label: 'Cinza Quente' },
    { id: 'beige',         hex: '#78716C', label: 'Bege' },
    { id: 'warm-brown',    hex: '#6B5B45', label: 'Marrom Quente' },
    // Roxo
    { id: 'purple-deep',   hex: '#4C1D95', label: 'Roxo Profundo' },
    { id: 'purple-soft',   hex: '#5B21B6', label: 'Roxo Suave' },
];

export interface DeckItem {
    _id: string;
    name: string;
    description?: string;
    iconUrl?: string | null;
    iconId?: string | null;
    subdeckCount: number;
    totalCards: number;
    isBlocked?: boolean;
    purchased?: boolean;
    isFree?: boolean;
    allowFreeEntry?: boolean;
    isHidden?: boolean;
    isCustom?: boolean;
    tags?: string[];
    createdAt?: string;
    updatedAt?: string;
    colorId?: string;
    masteryPercentage?: number;
}

interface DeckGridProps {
    decks: DeckItem[];
    isAdmin: boolean;
    isEditor?: boolean;
    searchQuery?: string;
    onNavigate: (deck: DeckItem) => void;
    onEdit: (deck: DeckItem) => void;
    onMove: (deck: DeckItem) => void;
    onCopy?: (deck: DeckItem) => void;
    onDelete: (deck: DeckItem) => void;
    onExport: (deckId: string) => void;
    onToggleBlock: (deckId: string) => void;
    onToggleHide: (deckId: string) => void;
    onReorder?: (decks: DeckItem[]) => void;
    onUpgradeClick?: (deck: DeckItem) => void;
    onInterdictedClick?: (deck: DeckItem) => void;
    onToggleFree: (deckId: string) => void;
    emptyMessage?: string;
    emptyAction?: { label: string; onClick: () => void };
}

// ── Sortable Deck Card ──
interface CardProps {
    deck: DeckItem;
    isAdmin: boolean;
    onEdit: () => void;
    onDelete: () => void;
    onExport: () => void;
    onToggleBlock: () => void;
    onToggleHide: () => void;
    onMove: () => void;
    onCopy?: () => void;
    onToggleFree: () => void;
}

const SortableDeckCard: React.FC<CardProps> = ({
    deck, isAdmin, onEdit, onDelete, onExport, onToggleBlock, onToggleFree, onToggleHide, onMove, onCopy
}) => {
    const [menuOpen, setMenuOpen] = useState(false);
    const [menuPos, setMenuPos] = useState({ top: 0, right: 0 });
    const kebabBtnRef = useRef<HTMLButtonElement>(null);
    const kebabMenuRef = useRef<HTMLDivElement>(null);

    const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
        useSortable({ id: deck._id, disabled: !isAdmin });

    const resolvedColor = DECK_COLORS[deck.colorId || 'blue-default'] ?? '#185FA5';

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.3 : 1,
        position: 'relative' as const,
        '--deck-color': resolvedColor,
    } as React.CSSProperties;

    const openMenu = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (kebabBtnRef.current) {
            const rect = kebabBtnRef.current.getBoundingClientRect();
            setMenuPos({
                top: rect.bottom + 4,
                right: window.innerWidth - rect.right,
            });
        }
        setMenuOpen(p => !p);
    };

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as Node;
            if (
                kebabBtnRef.current?.contains(target) ||
                kebabMenuRef.current?.contains(target)
            ) return;
            setMenuOpen(false);
        };
        if (menuOpen) document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [menuOpen]);

    const mastery = deck.masteryPercentage ?? 0;
    const IconComp = deck.iconId ? ICON_MAP[deck.iconId] : null;

    const kebabPortal = menuOpen ? ReactDOM.createPortal(
        <div
            ref={kebabMenuRef}
            className="dg-kebab-menu"
            style={{ position: 'fixed', top: menuPos.top, right: menuPos.right, zIndex: 9999 }}
        >
            <button className="dg-kebab-item" onClick={e => { e.stopPropagation(); setMenuOpen(false); onEdit(); }}>
                <Edit size={14} /> Editar
            </button>
            <button className="dg-kebab-item" onClick={e => { e.stopPropagation(); setMenuOpen(false); onExport(); }}>
                <Download size={14} /> Exportar
            </button>
            <button className="dg-kebab-item" onClick={e => { e.stopPropagation(); setMenuOpen(false); onToggleBlock(); }}>
                {deck.isBlocked
                    ? <><Unlock size={14} /> Disponibilizar</>
                    : <><Lock size={14} /> Interditar</>}
            </button>
            <button className="dg-kebab-item" onClick={e => { e.stopPropagation(); setMenuOpen(false); onMove(); }}>
                <ArrowRight size={14} /> Mover
            </button>
            {onCopy && (
                <button className="dg-kebab-item" onClick={e => { e.stopPropagation(); setMenuOpen(false); onCopy(); }}>
                    <Copy size={14} /> Copiar
                </button>
            )}
            <button className={`dg-kebab-item ${deck.isFree ? 'dg-kebab-item--free-on' : ''}`} onClick={e => { e.stopPropagation(); setMenuOpen(false); onToggleFree(); }}>
                <Gift size={14} /> {deck.isFree ? 'Remover Gratuidade' : 'Liberar Gratuito'}
            </button>
            <button className="dg-kebab-item" onClick={e => { e.stopPropagation(); setMenuOpen(false); onToggleHide(); }}>
                {deck.isHidden
                    ? <><Eye size={14} /> Mostrar</>
                    : <><EyeOff size={14} /> Esconder</>}
            </button>
            <div className="dg-kebab-divider" />
            <button className="dg-kebab-item dg-kebab-item--danger" onClick={e => { e.stopPropagation(); setMenuOpen(false); onDelete(); }}>
                <Trash2 size={14} /> Excluir
            </button>
        </div>,
        document.body
    ) : null;

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={`dg-card ${isDragging ? 'dg-card--dragging' : ''} ${deck.isBlocked ? 'dg-card--blocked' : ''} ${deck.isHidden ? 'dg-card--hidden' : ''} ${deck.isCustom ? 'dg-card--custom' : ''}`}
        >
            {/* Barra de cor no topo */}
            <div className="dg-color-bar" />

            {/* Header do card */}
            <div className="dg-card-header">
                <div className="dg-card-icon-wrap">
                    {IconComp
                        ? <IconComp size={20} className="dg-card-icon-placeholder" />
                        : deck.iconUrl
                            ? <img src={deck.iconUrl} alt={deck.name} className="dg-card-icon" />
                            : <BookOpen size={18} className="dg-card-icon-placeholder" />
                    }
                </div>

                <div className="dg-card-title-row">
                    <h3 className="dg-card-title">{deck.name}</h3>
                    {deck.isFree && (
                        <span className="dg-badge-free"><Gift size={11} /> Gratuito</span>
                    )}
                </div>

                {isAdmin && (
                    <div className="dg-card-admin">
                        <div
                            className="dg-drag-handle"
                            {...attributes}
                            {...listeners}
                            onClick={e => e.stopPropagation()}
                        >
                            <DragHandle isDragging={isDragging} />
                        </div>

                        <button
                            ref={kebabBtnRef}
                            className={`dg-kebab-btn ${menuOpen ? 'dg-kebab-btn--active' : ''}`}
                            onClick={openMenu}
                        >⋮</button>
                    </div>
                )}
            </div>

            {deck.description && (
                <p className="dg-card-desc">{deck.description}</p>
            )}

            {deck.tags && deck.tags.length > 0 && (
                <div className="dg-card-tags">
                    {deck.tags.map((tag, i) => (
                        <span key={i} className="dg-tag">
                            <Tag size={10} />
                            {tag}
                        </span>
                    ))}
                </div>
            )}

            <div className="dg-card-stats">
                <div className="dg-stat">
                    <span className="dg-stat-value">{deck.subdeckCount || 0}</span>
                    <span className="dg-stat-label">Subdecks</span>
                </div>
                <div className="dg-stat">
                    <span className="dg-stat-value">{deck.totalCards || 0}</span>
                    <span className="dg-stat-label">Cards</span>
                </div>
                {mastery > 0 && (
                    <div className="dg-stat">
                        <span className="dg-stat-value dg-stat-mastery">{mastery}%</span>
                        <span className="dg-stat-label">Domínio</span>
                    </div>
                )}
            </div>

            <div className="dg-mastery-wrap">
                <div className="dg-mastery-label-row">
                    <span className="dg-mastery-label">Domínio</span>
                    <span className="dg-mastery-pct">{mastery}%</span>
                </div>
                <div className="dg-mastery-segments">
                    {[0, 1, 2, 3, 4].map(i => {
                        const segFill = Math.min(100, Math.max(0, (mastery - i * 20) / 20 * 100));
                        return (
                            <div key={i} className="dg-mastery-seg">
                                <div className="dg-mastery-seg-fill" style={{ width: `${segFill}%` }} />
                            </div>
                        );
                    })}
                </div>
            </div>

            <div className="dg-card-cta">
                <button className="dg-btn-enter">
                    {deck.isBlocked && <Lock size={15} />}
                    Ver Subdecks
                </button>
            </div>

            {kebabPortal}
        </div>
    );
};

// ── DeckGrid principal ──
const DeckGrid: React.FC<DeckGridProps> = ({
    decks,
    isAdmin,
    isEditor = false,
    searchQuery = '',
    onNavigate,
    onEdit,
    onDelete,
    onMove,
    onCopy,
    onToggleFree,
    onExport,
    onToggleBlock,
    onToggleHide,
    onReorder,
    onUpgradeClick,
    onInterdictedClick,
    emptyMessage = 'Nenhum deck nesta aba.',
    emptyAction,
}) => {
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    const filtered = decks.filter(deck => {
        const term = searchQuery.trim().toLowerCase();
        if (!term) return true;
        return deck.name.toLowerCase().includes(term) ||
            (deck.description && deck.description.toLowerCase().includes(term)) ||
            (deck.tags && deck.tags.some(t => t.toLowerCase().includes(term)));
    });

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id || !onReorder) return;
        const oldIndex = decks.findIndex(d => d._id === active.id);
        const newIndex = decks.findIndex(d => d._id === over.id);
        onReorder(arrayMove(decks, oldIndex, newIndex));
    };

    if (filtered.length === 0) {
        return (
            <div className="dg-empty">
                <h2>{searchQuery ? 'Nenhum resultado encontrado.' : emptyMessage}</h2>
                {!searchQuery && emptyAction && (
                    <button className="dg-empty-btn" onClick={emptyAction.onClick}>
                        {emptyAction.label}
                    </button>
                )}
            </div>
        );
    }

    return (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={filtered.map(d => d._id)} strategy={rectSortingStrategy}>
                <div className="dg-grid">
                    {filtered.map(deck => (
                        <AccessControl
                            key={deck._id}
                            entityType="deck"
                            entityId={deck._id}
                            entityName={deck.name}
                            isInterdicted={deck.isBlocked || false}
                            isPurchased={deck.purchased || deck.allowFreeEntry || false}
                            isFree={deck.isFree || false}
                            isAdmin={isAdmin}
                            isEditor={isEditor}
                            onUpgradeClick={() => onUpgradeClick?.(deck)}
                            onInterdictedClick={() => onInterdictedClick?.(deck)}
                            onNavigate={() => onNavigate(deck)}
                        >
                            <SortableDeckCard
                                deck={deck}
                                isAdmin={isAdmin}
                                onEdit={() => onEdit(deck)}
                                onDelete={() => onDelete(deck)}
                                onExport={() => onExport(deck._id)}
                                onToggleBlock={() => onToggleBlock(deck._id)}
                                onToggleHide={() => onToggleHide(deck._id)}
                                onMove={() => onMove(deck)}
                                onCopy={onCopy ? () => onCopy(deck) : undefined}
                                onToggleFree={() => onToggleFree(deck._id)}
                            />
                        </AccessControl>
                    ))}
                </div>
            </SortableContext>
        </DndContext>
    );
};

export default DeckGrid;
